/**
 * CEL Onboarding Page
 * Landing page for the magic link from CEL onboarding email.
 * 
 * Flow:
 * 1. CEL clicks magic link → arrives at /onboarding/cel?token=...
 * 2. Page validates token
 * 3. CEL creates account (name, email, password)
 * 4. On success → redirects to wizard with HLOs loaded
 * 
 * This is the MINIMUM wiring needed to test the full activation loop.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, CheckCircle, AlertCircle, Rocket } from 'lucide-react';
import { CELOnboardingWizard } from './CELOnboardingWizard';
import { getApiUrl } from '../utils/apiClient';
import { publicAnonKey } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
type Phase = 'validating' | 'token_invalid' | 'create_account' | 'creating' | 'wizard' | 'complete';

interface TokenInfo {
  type: string;
  email: string;
  name: string;
  engagementId: string;
  companyId: string;
  expiresAt: string;
}

interface EngagementInfo {
  name: string;
  platform: string;
  consultantName?: string;
  consultantEmail?: string;
}

interface WizardHLO {
  id: string;
  text: string;
  category: 'efficiency' | 'automation' | 'user_experience' | 'reporting' | 'compliance' | 'integration' | 'other';
  source: 'ai_extracted' | 'client_added' | 'consultant_added';
  confidence?: number;
  status: 'draft' | 'approved' | 'removed';
}

function apiHeaders() {
  return {
    ...bearerAuthHeader(),
    'apikey': publicAnonKey,
    'Content-Type': 'application/json',
  };
}

export function CELOnboardingPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [phase, setPhase] = useState<Phase>('validating');
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [engagementInfo, setEngagementInfo] = useState<EngagementInfo | null>(null);
  const [hlos, setHlos] = useState<WizardHLO[]>([]);
  const [error, setError] = useState('');
  // Where "Go to Dashboard" leads: the firm's subdomain login, from complete-account.
  const [loginUrl, setLoginUrl] = useState('/login');

  // Account creation form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Step 1: Validate token on mount
  useEffect(() => {
    if (!token) {
      setError('No onboarding token provided. Please use the link from your email.');
      setPhase('token_invalid');
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = useCallback(async () => {
    try {
      console.log('[ACTIVATION_FLOW] Validating onboarding token...');
      const res = await fetch(getApiUrl(`/onboarding/validate/${token}`), {
        headers: {
          ...bearerAuthHeader(),
          'apikey': publicAnonKey,
        },
      });

      const data = await res.json();

      if (!res.ok || !data.valid) {
        setError(data.error || 'Invalid or expired token');
        setPhase('token_invalid');
        return;
      }

      setTokenInfo(data.token);
      console.log('[ACTIVATION_FLOW] Token valid, loading engagement data...');

      // Load engagement data for wizard context
      await loadEngagementData(data.token.engagementId);

      setPhase('create_account');
    } catch (err) {
      console.error('[ACTIVATION_FLOW] Token validation error:', err);
      setError('Failed to validate token. Please try again.');
      setPhase('token_invalid');
    }
  }, [token]);

  const loadEngagementData = async (engagementId: string) => {
    try {
      // Load HLOs for this engagement
      const hloRes = await fetch(getApiUrl(`/engagements/${engagementId}/hlos`), {
        headers: {
          ...bearerAuthHeader(),
          'apikey': publicAnonKey,
        },
      });

      if (hloRes.ok) {
        const hloData = await hloRes.json();
        if (hloData.hlos && Array.isArray(hloData.hlos)) {
          // Map API HLOs (title+description) to wizard HLO format (text)
          const mappedHlos: WizardHLO[] = hloData.hlos
            .filter((h: any) => h.status !== 'removed')
            .map((h: any) => ({
              id: h.id,
              text: h.description ? `${h.title}: ${h.description}` : h.title,
              category: h.category || 'other',
              source: h.source || 'ai_extracted',
              confidence: h.confidence,
              status: h.status || 'draft',
            }));
          setHlos(mappedHlos);
          console.log(`[ACTIVATION_FLOW] Loaded ${mappedHlos.length} HLOs for wizard`);
        }
      }

      // Try to load engagement details for context
      // Use the SOW status endpoint to get engagement info
      const sowRes = await fetch(getApiUrl(`/engagements/${engagementId}/sow-status`), {
        headers: {
          ...bearerAuthHeader(),
          'apikey': publicAnonKey,
        },
      });

      if (sowRes.ok) {
        const sowData = await sowRes.json();
        console.log(`[ACTIVATION_FLOW] SOW status: hasSOW=${sowData.hasSOW}`);
      }
    } catch (err) {
      console.error('[ACTIVATION_FLOW] Error loading engagement data:', err);
      // Non-fatal: wizard can work without HLOs
    }
  };

  // Step 2: Create account
  const handleCreateAccount = async () => {
    if (!tokenInfo) return;

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError('');
    setPhase('creating');

    try {
      console.log('[ACTIVATION_FLOW] Creating CEL account...');
      const res = await fetch(getApiUrl('/onboarding/complete-account'), {
        method: 'POST',
        headers: apiHeaders(),
        body: JSON.stringify({
          token,
          password,
          name: tokenInfo.name,
          email: tokenInfo.email,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to create account');
        setPhase('create_account');
        return;
      }

      console.log(`[ACTIVATION_FLOW] CEL account created: ${data.user.id}`);
      if (typeof data.loginUrl === 'string' && data.loginUrl) setLoginUrl(data.loginUrl);

      // Load engagement info for wizard
      setEngagementInfo({
        name: `Engagement ${tokenInfo.engagementId.substring(0, 8)}`,
        platform: 'Atlassian',
        consultantName: 'Your Consultant',
        consultantEmail: '',
      });

      console.log('[ACTIVATION_FLOW] onboarding wizard loaded with hlos');
      setPhase('wizard');
    } catch (err) {
      console.error('[ACTIVATION_FLOW] Account creation error:', err);
      setError('Failed to create account. Please try again.');
      setPhase('create_account');
    }
  };

  // Step 3: Wizard completion
  const handleWizardComplete = async (data: { approvedHLOs: any[]; teamMembers: any[] }) => {
    console.log('[ACTIVATION_FLOW] Wizard completed', {
      approvedHLOs: data.approvedHLOs.length,
      teamMembers: data.teamMembers.length,
    });

    // Persist HLOs on the server
    if (tokenInfo) {
      for (const hlo of data.approvedHLOs) {
        try {
          // Client-added HLOs need to be created first (they only exist in local state)
          if (hlo.source === 'client_added') {
            console.log(`[HLO_SAVE] Creating client-added HLO: ${hlo.text}`);
            await fetch(getApiUrl(`/engagements/${tokenInfo.engagementId}/hlos`), {
              method: 'POST',
              headers: apiHeaders(),
              body: JSON.stringify({
                title: hlo.text,
                description: '',
                category: hlo.category || 'other',
                source: 'client_added',
              }),
            });
          } else {
            // Existing HLOs — update status to approved
            console.log(`[HLO_SAVE] Approving existing HLO: ${hlo.id}`);
            await fetch(getApiUrl(`/engagements/${tokenInfo.engagementId}/hlos/${hlo.id}`), {
              method: 'PUT',
              headers: apiHeaders(),
              body: JSON.stringify({ status: 'approved' }),
            });
          }
        } catch (err) {
          console.error(`[HLO_SAVE] Failed to save HLO ${hlo.id}:`, err);
        }
      }
      console.log(`[HLO_TRANSFER] ${data.approvedHLOs.length} HLOs persisted for engagement ${tokenInfo.engagementId}`);
    }

    setPhase('complete');
  };

  // ==========================================
  // RENDER
  // ==========================================

  if (phase === 'validating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Validating your onboarding link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'token_invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Invalid Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <p className="mt-4 text-sm text-gray-600">
              Please contact your consultant for a new onboarding link.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'create_account' || phase === 'creating') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="w-6 h-6 text-blue-600" />
              <CardTitle>Create Your Account</CardTitle>
            </div>
            <CardDescription>
              Welcome, {tokenInfo?.name}! Set up your PathKit account to get started.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Name</Label>
              <Input value={tokenInfo?.name || ''} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label className="text-sm font-medium">Email</Label>
              <Input value={tokenInfo?.email || ''} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                disabled={phase === 'creating'}
              />
            </div>
            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                disabled={phase === 'creating'}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleCreateAccount}
              disabled={phase === 'creating' || !password || !confirmPassword}
              className="w-full"
            >
              {phase === 'creating' ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account & Start Onboarding'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'wizard' && tokenInfo) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-3xl mx-auto">
          <CELOnboardingWizard
            engagementId={tokenInfo.engagementId}
            engagementName={engagementInfo?.name || 'Your Engagement'}
            platform={engagementInfo?.platform || 'Atlassian'}
            consultantName={engagementInfo?.consultantName || 'Your Consultant'}
            consultantEmail={engagementInfo?.consultantEmail || ''}
            initialHLOs={hlos}
            onComplete={handleWizardComplete}
          />
        </div>
      </div>
    );
  }

  if (phase === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Onboarding Complete!</h2>
            <p className="text-gray-600 mb-4">
              Your consultant team has been notified. The discovery process will begin shortly.
            </p>
            <Button onClick={() => { window.location.href = loginUrl; }}>
              Go to Dashboard
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              Sign in with {tokenInfo?.email || 'your email'} and the password you just chose.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}